import React, { useContext, useEffect, useState } from 'react'
import { AxiosInstance, AxiosResponse } from 'axios'
import { Button, CircularProgress, Link, Menu, MenuItem, TextField, Typography } from '@material-ui/core'
import Snackbar from 'src/components/Snackbar'
import Alert from '@material-ui/lab/Alert'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import 'src/assets/css/components/schemaSection.css'
import { APIContext } from 'src/contexts/api'
import { Stream } from 'src/models/IDataComponent'
import { copyTextAsync } from 'src/utils/clipboard'
import { logError } from 'src/utils/Error'
import {
    isSelected, ISourceSchema, SchemaFormat, schemaFileName, schemaFormats, schemaFormatURI, selectionLabel,
} from 'src/utils/schemaExport'

interface ISchemaSectionProps {
    pipelineName: string
    /** the pipeline's sources with discovered streams: none until a discovery finds some */
    sources: ISourceSchema[]
    /**
     * the pipeline's `schema` link: its streams as its runs select them, and every export format.
     * Without it (an older catalog) the tab shows the discovered selection and offers no exports.
     */
    schemaHref?: string
    /** changes when what the schema is read from does: the streams, or the pipeline's select rules */
    refreshKey?: string
    /** whether the user can start a discovery: the pipeline has a 'discover pipeline' link */
    canDiscover: boolean
    /** a discovery (or a Verify, which discovers too) is queued or running */
    isDiscovering: boolean
    onDiscover: () => void
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/** A dropdown of the export formats, for copying or downloading the schema in one of them. */
const FormatMenu = (props: { label: string, onSelect: (format: SchemaFormat) => void }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    return <>
        <Button
            variant='outlined'
            size='small'
            className='schema-export-button'
            aria-haspopup='menu'
            endIcon={<ArrowDropDownIcon />}
            onClick={e => setAnchorEl(e.currentTarget)}>
            {props.label}
        </Button>
        <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            disableScrollLock
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClose={() => setAnchorEl(null)}>
            {schemaFormats.map(({ format, label }) =>
                <MenuItem
                    key={format}
                    dense
                    onClick={() => { setAnchorEl(null); props.onSelect(format) }}>
                    {label}
                </MenuItem>)}
        </Menu>
    </>
}

const StreamRow = ({ stream }: { stream: Stream }) => {
    const [isOpen, setOpen] = useState(false)
    const selected = isSelected(stream)
    const selectedFields = stream.fields.filter(isSelected).length

    return <li className={`schema-stream ${selected ? '' : 'excluded'}`}>
        <button
            type='button'
            className='schema-stream-header'
            aria-expanded={isOpen}
            disabled={!stream.fields.length}
            onClick={() => setOpen(o => !o)}>
            <i className={`fas fa-chevron-right schema-stream-chevron ${isOpen ? 'open' : ''}`} />
            <span className='schema-stream-name'>{stream.name}</span>
            <span className={`schema-selection ${stream.selected.toLowerCase()}`}>{selectionLabel(stream)}</span>
            <span className='schema-stream-count'>
                {selected && selectedFields !== stream.fields.length
                    ? `${selectedFields} of ${plural(stream.fields.length, 'field')}`
                    : plural(stream.fields.length, 'field')}
            </span>
        </button>
        {isOpen &&
            <ul className='schema-fields' aria-label={`${stream.name} fields`}>
                {stream.fields.map(field =>
                    <li
                        key={field.name}
                        className={`schema-field ${isSelected(field) ? '' : 'excluded'}`}
                        title={selectionLabel(field)}>
                        {field.name}
                    </li>)}
            </ul>}
    </li>
}

/*
 * The Streams tab of a pipeline row: the streams its sources were last found to have, which can be
 * copied or downloaded as Markdown, JSON, a Singer catalog or dbt sources (MEL-702).
 */
const SchemaSection = (props: ISchemaSectionProps): JSX.Element => {
    const { pipelineName, schemaHref, refreshKey, canDiscover, isDiscovering, onDiscover } = props
    const { catalogAPI } = useContext(APIContext)
    const api = catalogAPI as AxiosInstance
    const [filter, setFilter] = useState('')
    // the catalog's reading of the streams, which applies the pipeline's select rules as its runs do
    const [schema, setSchema] = useState<ISourceSchema[] | undefined>(undefined)
    const hasStreams = props.sources.length > 0
    // kept once the snackbar closes, so it doesn't empty while it fades out
    const [message, setMessage] = useState<{ text: string, severity: 'success' | 'error' }>({ text: '', severity: 'success' })
    const [isMessageOpen, setMessageOpen] = useState(false)
    const notify = (text: string, severity: 'success' | 'error') => {
        setMessage({ text, severity })
        setMessageOpen(true)
    }

    useEffect(() => {
        if (!schemaHref || !hasStreams)
            return
        let current = true
        api.get(schemaHref)
            .then((r: AxiosResponse<{ sources: ISourceSchema[] }>) => { if (current) setSchema(r.data.sources) })
            // the discovered selection is still worth showing
            .catch(logError)
        return () => { current = false }
    }, [api, schemaHref, hasStreams, refreshKey])

    if (!hasStreams) {
        return <div className='datastore-properties schema-section'>
            {isDiscovering
                ? <Typography component='div' className='schema-empty discovering' color='textSecondary' data-testid='schema-discovering'>
                    <CircularProgress size={14} />
                    Discovering the source’s streams…
                </Typography>
                : <Typography className='schema-empty' color='textSecondary' data-testid='schema-not-discovered'>
                    {canDiscover
                        ? <>Streams haven’t been discovered yet, <Link
                            component='button'
                            variant='body1'
                            className='schema-discover-link'
                            onClick={onDiscover}>click here</Link> to start discovery.</>
                        : 'Streams haven’t been discovered yet.'}
                </Typography>}
        </div>
    }

    const label = (format: SchemaFormat) => schemaFormats.find(f => f.format === format)!.label

    const sources = schema || props.sources

    // the text as the catalog writes it, untouched: axios would parse the JSON formats
    const fetchFormat = (format: SchemaFormat): Promise<{ text: string, type: string }> =>
        api.get(schemaFormatURI(schemaHref!, format), { responseType: 'text', transformResponse: (data: string) => data })
            .then((r: AxiosResponse<string>) => ({ text: r.data, type: String(r.headers?.['content-type'] || 'text/plain') }))

    // started inside the click, so the clipboard write keeps its user gesture (copyTextAsync)
    const handleCopy = (format: SchemaFormat) => copyTextAsync(fetchFormat(format).then(r => r.text))
        .then(() => notify(`Copied as ${label(format)}`, 'success'))
        .catch(e => {
            logError(e)
            notify('Could not copy the streams — please try again', 'error')
        })

    const handleDownload = (format: SchemaFormat) => fetchFormat(format)
        .then(({ text, type }) => {
            // a blob URL, as the log download (Pipelines.downloadLog)
            const url = URL.createObjectURL(new Blob([text], { type }))
            const link = document.createElementNS('http://www.w3.org/1999/xhtml', 'a') as HTMLAnchorElement
            link.href = url
            link.download = schemaFileName(pipelineName, format)
            link.click()
            URL.revokeObjectURL(url)
        })
        .catch(e => {
            logError(e)
            notify('Could not download the streams — please try again', 'error')
        })

    const streams = ([] as Stream[]).concat(...sources.map(s => s.streams))
    const selectedStreams = streams.filter(isSelected).length
    const fieldCount = streams.reduce((n, s) => n + s.fields.length, 0)
    const needle = filter.trim().toLowerCase()
    const matches = (stream: Stream) => !needle
        || stream.name.toLowerCase().includes(needle)
        || stream.fields.some(f => f.name.toLowerCase().includes(needle))

    return <div className='datastore-properties schema-section'>
        <div className='schema-toolbar'>
            <div className='schema-summary' data-testid='schema-summary'>
                <span><strong>{streams.length}</strong> {streams.length === 1 ? 'stream' : 'streams'}</span>
                <span><strong>{selectedStreams}</strong> selected</span>
                <span><strong>{fieldCount}</strong> {fieldCount === 1 ? 'field' : 'fields'}</span>
            </div>
            <TextField
                className='schema-filter'
                size='small'
                variant='outlined'
                placeholder='Filter streams or fields'
                inputProps={{ 'aria-label': 'Filter streams or fields' }}
                value={filter}
                onChange={e => setFilter(e.target.value)} />
            {schemaHref && <>
                <FormatMenu label='Copy As' onSelect={handleCopy} />
                <FormatMenu label='Download As' onSelect={handleDownload} />
            </>}
        </div>
        {sources.map(source => {
            const shown = source.streams.filter(matches)
            return <section key={source.name} className='schema-source'>
                {sources.length > 1 && <h3 className='schema-source-name'>{source.name}</h3>}
                {shown.length
                    ? <ul className='schema-streams' aria-label={`${source.name} streams`}>
                        {shown.map(stream => <StreamRow key={stream.name} stream={stream} />)}
                    </ul>
                    : <Typography className='schema-empty' color='textSecondary'>No streams match ‘{filter.trim()}’.</Typography>}
            </section>
        })}
        <Snackbar
            open={isMessageOpen}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            onClose={() => setMessageOpen(false)}>
            <Alert severity={message.severity}>{message.text}</Alert>
        </Snackbar>
    </div>
}

export default SchemaSection
